'use client';
import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import React from 'react';

import R2Image from '@/components/ui/R2Image';
import BaseHeroSection from '@/components/sections/BaseHeroSection';
import HeroSearchBar from '@/components/sections/HeroSearchBar';

import { normalizeGradientHeroSectionData } from './gradientHeroData';
import type { GradientHeroSectionData } from './GradientHeroSection.types';
import { scrollToTestId } from '@/utils/scrollToTestId';
interface GradientHeroSectionProps {
  data?: GradientHeroSectionData;
  showSearch?: boolean;
  customButtonActions?: Record<
    string,
    (context: {
      button: NonNullable<GradientHeroSectionData['buttons']>[number];
      index: number;
      event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>;
    }) => void
  >;
}

const FloatingImage: React.FC<{
  path: string;
  alt: string;
  sizes: string;
  wrapperStyle: React.CSSProperties;
  offsetX: number;
  offsetY: number;
  borderRadius?: string;
  className?: string;
  priority?: boolean;
}> = ({
  path,
  alt,
  sizes,
  wrapperStyle,
  offsetX,
  offsetY,
  borderRadius = '100px',
  className = '',
  priority,
}) => (
  <div className={`${className} relative`} style={wrapperStyle}>
    <div
      aria-hidden="true"
      className="border-theme-primary pointer-events-none absolute inset-0 z-0 border"
      style={{
        borderRadius,
        transform: `translate(${offsetX}px, ${offsetY}px)`,
      }}
    />

    <div
      className="relative z-10 h-full w-full overflow-hidden"
      style={{ borderRadius }}
    >
      <R2Image
        path={path}
        alt={alt}
        sizes={sizes}
        containerClassName="relative h-full w-full"
        priority={priority}
      />
    </div>
  </div>
);

const CANVAS_ASPECT = 433 / 491; // ≈ 0.88

const MAIN_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '0%',
  top: '0%',
  width: '45.3%',
  height: '75.6%',
};

const TOP_RIGHT_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '61.7%',
  top: '5.1%',
  width: '32.3%',
  height: '47.7%',
};

const BOTTOM_RIGHT_STYLE: React.CSSProperties = {
  position: 'absolute',
  left: '41.6%',
  top: '49.9%',
  width: '32.3%',
  height: '47.7%',
};

const GradientHeroSection: React.FC<GradientHeroSectionProps> = ({
  data,
  showSearch = false,
  customButtonActions = {},
}) => {
  const router = useRouter();
  const handleButtonClick = useCallback(
    (
      button: NonNullable<GradientHeroSectionData['buttons']>[number],
      index: number,
      event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    ) => {
      const action = button.action;

      if (!action) return;

      if (action.type === 'scrollToTestId') {
        event.preventDefault();

        scrollToTestId(action.testId, {
          offset: action.offset,
          behavior: action.behavior,
          focus: action.focus,
        });

        return;
      }

      if (action.type === 'custom') {
        const handler = customButtonActions?.[action.key];

        if (!handler) return;

        event.preventDefault();

        handler({
          button,
          index,
          event,
        });
      }
      if (action.type === 'scrollOnNextPage') {
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        const targetId = action.targetId ?? action.testId;

        if (!targetId) return;

        const href = button.link;

        if (!href) return;

        event.preventDefault();

        let targetHref = href;

        // If targetId is provided and not already present as a hash in href, append it
        if (targetId && !targetHref.includes('#')) {
          const [basePath, search] = targetHref.split('?');
          const queryPart = search ? `?${search}` : '';
          targetHref = `${basePath}#${targetId}${queryPart}`;
        }

        router.push(targetHref, { scroll: false });

        return;
      }
    },
    [customButtonActions, router],
  );
  if (!data) return null;

  const {
    heading,
    description,
    buttons,
    link,
    searchBar,
    images = [],
    badge,
    textsContentData,
  } = normalizeGradientHeroSectionData(data);

  const [main, topRight, bottomRight] = images;

  const collage =
    images.length > 0 ? (
      <>
        <div
          aria-hidden="true"
          className="h-hero-collage-mobile-height w-hero-collage-mobile-width pointer-events-none relative z-0 mx-auto mt-8 mb-4 lg:hidden"
        >
          {main && (
            <FloatingImage
              path={main.src}
              alt={main.alt}
              sizes="80.2729263305664px"
              wrapperStyle={{
                position: 'absolute',
                right: '14px',
                top: '0px',
                width: '80.2729263305664px',
                height: '151.94517517089844px',
              }}
              offsetX={7}
              offsetY={7}
              borderRadius="72px"
              priority
            />
          )}

          {topRight && (
            <FloatingImage
              path={topRight.src}
              alt={topRight.alt}
              sizes="63.112728118896484px"
              wrapperStyle={{
                position: 'absolute',
                right: '0px',
                top: '120px',
                width: '63.112728118896484px',
                height: '105.4884033203125px',
              }}
              offsetX={6}
              offsetY={6}
              borderRadius="58px"
            />
          )}

          {bottomRight && (
            <FloatingImage
              path={bottomRight.src}
              alt={bottomRight.alt}
              sizes="53.9744338989257px"
              wrapperStyle={{
                position: 'absolute',
                left: '50px',
                top: '190px',
                width: '53.9744338989257px',
                height: '90.21440124511719px',
              }}
              offsetX={6}
              offsetY={6}
              borderRadius="52px"
            />
          )}
        </div>

        <div
          aria-hidden="true"
          className="h-hero-canvas-height w-hero-canvas-width relative hidden shrink-0 lg:block"
          style={{
            aspectRatio: CANVAS_ASPECT,
          }}
        >
          {main && (
            <FloatingImage
              path={main.src}
              alt={main.alt}
              sizes="(max-width: 1280px) 20vw, 200px"
              wrapperStyle={MAIN_STYLE}
              offsetX={20}
              offsetY={20}
              priority
            />
          )}

          {topRight && (
            <FloatingImage
              path={topRight.src}
              alt={topRight.alt}
              sizes="(max-width: 1280px) 15vw, 144px"
              wrapperStyle={TOP_RIGHT_STYLE}
              offsetX={20}
              offsetY={20}
            />
          )}

          {bottomRight && (
            <FloatingImage
              path={bottomRight.src}
              alt={bottomRight.alt}
              sizes="(max-width: 1280px) 15vw, 144px"
              wrapperStyle={BOTTOM_RIGHT_STYLE}
              offsetX={20}
              offsetY={20}
            />
          )}
        </div>
      </>
    ) : null;

  return (
    <BaseHeroSection
      testId="gradient-hero-section"
      heading={heading}
      description={description}
      buttons={buttons}
      link={link}
      badge={badge}
      textsContentData={textsContentData}
      onButtonClick={handleButtonClick}
      // trustLogos={trustLogos}
      horizontalContentAlignment={images.length > 0 ? 'left' : 'center'}
      sideContent={collage}
      className={showSearch ? 'z-10' : undefined}
      // style={{ minHeight: '45rem' }}
    >
      {showSearch && searchBar && (
        <HeroSearchBar
          inputPlaceholder={searchBar.inputPlaceholder}
          searchButtonLabel={searchBar.searchButtonLabel}
        />
      )}
    </BaseHeroSection>
  );
};

export default GradientHeroSection;
